import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { MapPin, Mail, Phone, User, X } from "lucide-react";

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HIGHLIGHT = [
  { name: "Florida", coordinates: [-81.7, 28.0] },
  { name: "Texas", coordinates: [-99.3, 31.3] },
  { name: "Illinois", coordinates: [-89.2, 40.0] },
  { name: "Michigan", coordinates: [-84.7, 43.4] },
  { name: "Ohio", coordinates: [-82.8, 40.3] },
  { name: "Georgia", coordinates: [-83.4, 32.7] },
  { name: "South Carolina", coordinates: [-80.9, 33.9] },
  { name: "Missouri", coordinates: [-92.5, 38.4] },
  { name: "Indiana", coordinates: [-86.3, 39.9] },
];
const HIGHLIGHT_SET = new Set(HIGHLIGHT.map((s) => s.name));
const ABBR = {
  FL: "Florida", TX: "Texas", IL: "Illinois", MI: "Michigan", OH: "Ohio",
  GA: "Georgia", SC: "South Carolina", MO: "Missouri", IN: "Indiana",
};
const normState = (s) => {
  if (!s) return "";
  const t = s.trim();
  return ABBR[t.toUpperCase()] || t;
};

export default function USMap() {
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const [churches, setChurches] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/churches`).then((r) => setChurches(r.data)).catch(() => {});
  }, []);

  const byState = useMemo(() => {
    const map = {};
    churches.forEach((c) => {
      const full = normState(c.state);
      const key = HIGHLIGHT.find((h) => h.name.toLowerCase() === full.toLowerCase())?.name || full;
      if (!map[key]) map[key] = [];
      map[key].push(c);
    });
    return map;
  }, [churches]);

  const pick = (name) => setSelected((prev) => (prev === name ? null : name));
  const selectedChurches = selected ? byState[selected] || [] : [];

  return (
    <div data-testid="us-network-map">
      <div className="relative border border-border bg-[#0a0a0a] overflow-hidden">
        <ComposableMap projection="geoAlbersUsa" projectionConfig={{ scale: 1000 }} width={900} height={500} style={{ width: "100%", height: "auto" }}>
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const active = HIGHLIGHT_SET.has(geo.properties.name);
                const isSel = selected === geo.properties.name;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => active && setHovered(geo.properties.name)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => active && pick(geo.properties.name)}
                    style={{
                      default: {
                        fill: isSel ? "#C9A24B" : active ? "hsl(357 73% 31%)" : "#161616",
                        stroke: "#2c2c2c", strokeWidth: 0.6, outline: "none",
                        cursor: active ? "pointer" : "default",
                      },
                      hover: {
                        fill: active ? "hsl(357 66% 44%)" : "#1e1e1e",
                        stroke: "#C9A24B", strokeWidth: 0.9, outline: "none",
                        cursor: active ? "pointer" : "default",
                      },
                      pressed: { outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {HIGHLIGHT.map((s) => (
            <Marker key={s.name} coordinates={s.coordinates} onClick={() => pick(s.name)} style={{ default: { cursor: "pointer" } }}>
              <g transform="translate(-6, -12)" style={{ cursor: "pointer" }}>
                <path d="M6 0C2.7 0 0 2.7 0 6c0 4.5 6 12 6 12s6-7.5 6-12c0-3.3-2.7-6-6-6z"
                  fill={selected === s.name ? "hsl(357 73% 31%)" : "#C9A24B"} stroke="#0a0a0a" strokeWidth={0.8} />
                <circle cx="6" cy="6" r="2.2" fill="#0a0a0a" />
              </g>
            </Marker>
          ))}
        </ComposableMap>

        <div className="absolute top-4 left-4 pointer-events-none">
          <span className="overline text-secondary text-[0.6rem]">
            {hovered ? `${hovered} — Tap to view churches` : "9 States · Tap a state"}
          </span>
        </div>
      </div>

      {/* Legend of states (clickable) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border border border-border border-t-0">
        {HIGHLIGHT.map((s) => {
          const count = (byState[s.name] || []).length;
          return (
            <button
              key={s.name}
              onMouseEnter={() => setHovered(s.name)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => pick(s.name)}
              className={`flex items-center justify-between gap-2 bg-background px-4 py-3 text-left transition-colors duration-300 hover:bg-muted/50 ${selected === s.name ? "bg-muted/60" : ""}`}
              data-testid={`state-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <span className="flex items-center gap-2">
                <MapPin size={14} className="text-secondary shrink-0" />
                <span className="font-body text-sm text-foreground">{s.name}</span>
              </span>
              {count > 0 && <span className="overline text-secondary text-[0.55rem]">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Selected state church panel */}
      {selected && (
        <div className="border border-border border-t-0 bg-muted/20 p-6" data-testid="state-church-panel">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display uppercase text-2xl text-foreground">{selected}</h3>
            <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-primary" data-testid="close-state-panel">
              <X size={20} />
            </button>
          </div>
          {selectedChurches.length === 0 ? (
            <p className="font-editorial italic text-xl text-muted-foreground">
              No churches listed for {selected} yet — check back soon.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedChurches.map((c) => (
                <div key={c.id} className="border border-border bg-background p-5" data-testid="church-card">
                  <p className="font-display uppercase text-lg text-foreground">{c.name}</p>
                  {(c.address || c.city) && (
                    <p className="font-body text-sm text-muted-foreground mt-2 flex items-start gap-2">
                      <MapPin size={14} className="text-secondary shrink-0 mt-0.5" />
                      {[c.address, c.city, c.state].filter(Boolean).join(", ")}
                    </p>
                  )}
                  {c.pastor && <p className="font-body text-sm text-muted-foreground mt-1 flex items-center gap-2"><User size={14} className="text-secondary" />{c.pastor}</p>}
                  {c.email && <a href={`mailto:${c.email}`} className="font-body text-sm text-secondary hover:text-foreground mt-1 flex items-center gap-2"><Mail size={14} />{c.email}</a>}
                  {c.phone && <a href={`tel:${c.phone}`} className="font-body text-sm text-secondary hover:text-foreground mt-1 flex items-center gap-2"><Phone size={14} />{c.phone}</a>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

